import {
  BugOutlined,
  HomeOutlined,
  InfoCircleOutlined,
  ReloadOutlined,
  WarningOutlined,
} from '@ant-design/icons';
import { Button, Card, Collapse, Result, Space, Typography } from 'antd';
import type { CollapseProps } from 'antd';
import { Component, type ErrorInfo, type ReactNode } from 'react';
import { COLORS, SPACING, TYPOGRAPHY } from '../../constants/design';
import { ROUTE_PATHS } from '../../constants/routes';

const { Text, Paragraph } = Typography;

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
  errorId: string;
}

// TEMP: Always show detailed error info, even in prod, to help debug issues.
// Later, you can switch this back to an env‑based flag if needed.
const SHOW_ERROR_DETAILS = true;

class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      errorId: '',
    };
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    const errorId = `ERR-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;
    return {
      hasError: true,
      error,
      errorId,
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    this.setState({
      error,
      errorInfo,
    });

    // Log error to monitoring service
    console.error('ErrorBoundary caught an error:', error, errorInfo);

    // Call custom error handler if provided
    if (this.props.onError) {
      this.props.onError(error, errorInfo);
    }

    // Report to error tracking service (e.g., Sentry)
    // Sentry.captureException(error, { extra: errorInfo });
  }

  handleReload = () => {
    window.location.reload();
  };

  handleGoHome = () => {
    window.location.href = ROUTE_PATHS.DASHBOARD;
  };

  handleReset = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
      errorId: '',
    });
  };

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <div
          style={{
            minHeight: '100vh',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: COLORS.gray[50],
            padding: SPACING.lg,
          }}
        >
          <Card
            style={{
              maxWidth: '600px',
              width: '100%',
              boxShadow: '0 8px 24px rgba(0, 0, 0, 0.12)',
              borderRadius: SPACING.md,
            }}
          >
            <Result
              status="error"
              icon={<BugOutlined style={{ color: COLORS.error[500] }} />}
              title={
                <Text strong style={{ fontSize: TYPOGRAPHY.fontSize.xl, color: COLORS.gray[900] }}>
                  Something went wrong
                </Text>
              }
              subTitle={
                <div>
                  <Paragraph style={{ color: COLORS.gray[600], marginBottom: SPACING.lg }}>
                    We're sorry! An unexpected error occurred. Our team has been notified and is
                    working to fix this issue.
                  </Paragraph>

                  <Space direction="vertical" style={{ width: '100%' }} size="middle">
                    <div
                      style={{
                        background: COLORS.gray[100],
                        padding: SPACING.md,
                        borderRadius: SPACING.sm,
                        border: `1px solid ${COLORS.gray[200]}`,
                      }}
                    >
                      <Space align="start">
                        <InfoCircleOutlined style={{ color: COLORS.info[500], marginTop: '2px' }} />
                        <div>
                          <Text strong style={{ display: 'block', marginBottom: SPACING.xs }}>
                            Error ID: {this.state.errorId}
                          </Text>
                          <Text
                            style={{ fontSize: TYPOGRAPHY.fontSize.sm, color: COLORS.gray[600] }}
                          >
                            Please include this ID when reporting the issue to support.
                          </Text>
                        </div>
                      </Space>
                    </div>

                    {SHOW_ERROR_DETAILS && this.state.error && (
                      <Collapse
                        ghost
                        items={[
                          {
                            key: '1',
                            label: (
                              <Space>
                                <WarningOutlined style={{ color: COLORS.warning[500] }} />
                                <Text strong>Developer Information</Text>
                              </Space>
                            ),
                            children: (
                              <div
                                style={{
                                  background: COLORS.gray[900],
                                  color: COLORS.gray[100],
                                  padding: SPACING.md,
                                  borderRadius: SPACING.sm,
                                  fontFamily: 'monospace',
                                  fontSize: TYPOGRAPHY.fontSize.sm,
                                  whiteSpace: 'pre-wrap',
                                  overflow: 'auto',
                                  maxHeight: '200px',
                                }}
                              >
                                <Text
                                  strong
                                  style={{
                                    color: COLORS.error[400],
                                    display: 'block',
                                    marginBottom: SPACING.sm,
                                  }}
                                >
                                  Error: {this.state.error.name}
                                </Text>
                                <Text
                                  style={{
                                    color: COLORS.gray[300],
                                    display: 'block',
                                    marginBottom: SPACING.sm,
                                  }}
                                >
                                  Message: {this.state.error.message}
                                </Text>
                                {this.state.error.stack && (
                                  <div>
                                    <Text
                                      strong
                                      style={{
                                        color: COLORS.warning[400],
                                        display: 'block',
                                        marginBottom: SPACING.xs,
                                      }}
                                    >
                                      Stack Trace:
                                    </Text>
                                    <div
                                      style={{
                                        color: COLORS.gray[400],
                                        fontSize: TYPOGRAPHY.fontSize.xs,
                                      }}
                                    >
                                      {this.state.error.stack}
                                    </div>
                                  </div>
                                )}
                              </div>
                            ),
                          },
                        ]}
                      />
                    )}
                  </Space>
                </div>
              }
              extra={
                <Space wrap>
                  <Button
                    type="default"
                    icon={<ReloadOutlined />}
                    onClick={this.handleReload}
                    size="large"
                  >
                    Reload Page
                  </Button>
                  <Button
                    type="primary"
                    icon={<HomeOutlined />}
                    onClick={this.handleGoHome}
                    size="large"
                  >
                    Go to Dashboard
                  </Button>
                  {SHOW_ERROR_DETAILS && (
                    <Button type="dashed" onClick={this.handleReset} size="large">
                      Try Again
                    </Button>
                  )}
                </Space>
              }
            />
          </Card>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
